import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NodeAppBar from "./NodeAppBar";
import Loader from "../../../../common/Loader";
import RevisionTable from "../RevisionTable";
import TrainingLogTable from "../TrainingLogTable";
import BOMTable from "../BOMTable";
import RenderRevisionNodes from "../RenderRevisionNodes";
import useViewDocumentData from "../../hooks/useViewDocumentData";
import { setTaskReference } from "../../../Tasks/Uitlities";
import { pingTask } from "../../../../util/apiService";
import useApiTabLock from "../../../../hooks/useApiTabLock";
import { useRevisionLock } from "../../../../contexts/RevisionLockContext";
import { getCurrentStatus } from "../../../../util/AppUtils.jsx";
import { isLocked } from "../../Utils/utils";

/**
 * ViewDocument Component
 * 
 * Read-only view of complete revision details including:
 * - Revision Table
 * - Training Log Table
 * - BOM Table
 * - All hierarchical nodes (Sections, Steps, Substeps, Child nodes)
 * 
 * Uses custom hook for data fetching with loading state
 * 
 * Route: /view-revision/:part_id/:revision_id
 */
const ViewDocument = () => {
  const { part_id, revision_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);

  // Fetch all document data using custom hook
  const {
    loading,
    partData,
    revisionData,
    trainingLogs,
    bomTable,
  } = useViewDocumentData(part_id, revision_id);

  // State to track if we should temporarily bypass tab order lock
  // Initialize by checking if THIS specific tab has a bypass flag
  const [bypassTabOrderLock, setBypassTabOrderLock] = useState(() => {
    const tabId = sessionStorage.getItem('tab-id');
    const bypassFlag = sessionStorage.getItem(`tabOrderLockBypass_${revision_id}_${tabId}`);
    return bypassFlag === 'true';
  });

  // Use revision ID as route key to lock entire revision across all pages
  // This ensures all pages within the same revision (Document, sections, steps, substeps, canvases, etc.)
  // share the same lock across tabs
  // IMPORTANT: Use consistent routeKey across all revision-related pages
  const routeKey = `revision_${revision_id}`;

  // Tab lock management - only one tab can perform actions
  // Use revision-specific key to ensure all pages within the same revision share the same lock
  const { isLocked: isApiLocked, isActiveTab, lockedBy } = useApiTabLock(`revision_${revision_id}`);

  // Tab order lock from provider (same lock held when navigating Document <-> view-revision)
  const revisionLock = useRevisionLock();
  const isFirstTabByOrder =
    revisionLock?.revisionIdFromPath === revision_id ? revisionLock.isFirstTab : false;
  const isLockedByOrder =
    revisionLock?.revisionIdFromPath === revision_id ? revisionLock.isLocked : true;

  // Leadership restoration - Check if this tab was the leader before navigation
  useEffect(() => {
    const tabId = sessionStorage.getItem('tab-id');
    const fromDocument = sessionStorage.getItem(`fromDocument_${revision_id}`) === 'true';
    const wasLeader = sessionStorage.getItem(`wasLeader_${revision_id}_${tabId}`) === 'true';
    
    if (fromDocument && wasLeader) {
      setBypassTabOrderLock(true);
      sessionStorage.setItem(`tabOrderLockBypass_${revision_id}_${tabId}`, 'true');
    }
  }, [revision_id]);

  // Track when this tab becomes the leader
  useEffect(() => {
    const tabId = sessionStorage.getItem('tab-id');
    if (isFirstTabByOrder) {
      sessionStorage.setItem(`wasLeader_${revision_id}_${tabId}`, 'true');
    }
  }, [isFirstTabByOrder, revision_id]);

  // Find current revision using useMemo to avoid recalculating on every render
  // Must be called before any early returns (React Hooks rule)
  const currentRevision = useMemo(() => {
    if (partData && revisionData && revisionData.length > 0) {
      return revisionData.find(
        (rev) => rev.procedure_revision_id === revision_id
      );
    }
    return undefined;
  }, [partData, revisionData, revision_id]);

  const isRevEditableNot = isLocked(getCurrentStatus(currentRevision)) 
  // Set task reference for this document/revision - only when data changes
  // Must be called before any early returns (React Hooks rule)
  useEffect(() => {
    if (currentRevision && partData) {
      setTaskReference(
        dispatch,
        // `${partData.partNumber} Rev ${partData.revision}`,
        `Rev ${partData.revision} , ${partData.partNumber}`,
        currentRevision.procedure_revision_id,
        currentRevision?.tree_node_details?.tree_node_id,
        currentRevision?.tree_node_details?.ltree_path,
        true
      );
    }
  }, [currentRevision, partData, dispatch, revision_id]);

  // Start ping interval on component mount to keep lock alive
  useEffect(() => {
    if (!revision_id || !authUser) return;

    // Start ping interval immediately
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: revision_id,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed for view-revision:', error);
      }
    }, 60000); // 60 seconds = 1 minute

    // Handle browser back button to set flag when navigating back to Document.js
    const handlePopState = (event) => {
      // Check if we came from Document page
      const fromDocument = sessionStorage.getItem(`fromDocument_${revision_id}`) === 'true';
      
      if (fromDocument) {
        // Set flag to indicate we're returning back to Document.js from view-revision
        sessionStorage.setItem(`returningFromViewRevision_${revision_id}`, 'true');
        // Clean up the fromDocument flag
        sessionStorage.removeItem(`fromDocument_${revision_id}`);
        
        // Also dispatch custom event to notify Document.js
        window.dispatchEvent(new CustomEvent('navigation-from-view-revision', {
          detail: { revision_id, timestamp: Date.now() }
        }));
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Cleanup interval and event listener on unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      window.removeEventListener('popstate', handlePopState);
    };
  }, [revision_id, authUser]);

  // Show loader while fetching data
  if (loading) {
    return <Loader text="Loading revision details..." />;
  }

  // Show error message if data not found
  if (!partData) {
    return (
      <div style={{ padding: "20px" }}>
        <p>Revision data not found.</p>
      </div>
    );
  }

  // Handle Add dropdown navigation
  const handleAddElement = (type) => {
    if (type === "Section") {
      navigate(`/add-new-section/${partData.partNumber}/${partData.revision}/${part_id}/${revision_id}`);
    }
  };

  // Handle Edit button click - Navigate to edit procedure revision page
  const handleEditPart = () => {
    navigate(`/edit-procedure-revision/${revision_id}`);
  };

  return (
    <div>
      {/* Navigation Bar */}
      <NodeAppBar
        title={`${partData.type} ${partData.partNumber}`}
        isPart={true}
        isSelectedPage={true}
        onAddElement={handleAddElement}
        onEditPart={handleEditPart}
        partNumber={partData.partNumber}
        revision={partData.revision}
        type={partData.type}
        version_id_ref={currentRevision?.version_id_ref}
        ltree_path={currentRevision?.tree_node_details?.ltree_path}
        is_deleted={currentRevision?.is_deleted || false}
        isLocked={isRevEditableNot || (!bypassTabOrderLock && !isFirstTabByOrder) || (isApiLocked && lockedBy)}
        historyFilterDropdownDisabled
      />

      {/* Part Information Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 20px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            <input
              type="checkbox"
              checked={true}
              readOnly
            />
            <span style={{ fontSize: "16px", fontWeight: 600 }}>
              {partData.type} {partData.partNumber}
            </span>
          </label>
        </div>
      </div>

      {/* Main Content Section */}
      <div style={{ padding: "20px" }}>
        {/* Revisions Table - Read-only view */}
        <RevisionTable
          revisionData={revisionData}
          revisionId={revision_id}
          location={window.location}
          showCreateButton={false}
          isReadOnly={true}
        />

        {/* Training Log Table */}
        <TrainingLogTable trainingLogs={trainingLogs} />

        {/* BOM Table */}
        <BOMTable type={partData.type} bomTable={bomTable} />

        {/* Hierarchical Nodes - Disabled for viewing */}
        <RenderRevisionNodes
          partRef={part_id}
          procedureRevRef={revision_id}
          partNumber={partData.partNumber}
          revision={partData.revision}
          disabled={true}
          checkboxState={{
            allChildrenChecked: true,
            allChildrenDisabled: true,
          }}
        />

        {/* Spacer for bottom padding */}
        <div style={{ height: "100px" }}></div>
      </div>
    </div>
  );
};

export default ViewDocument;

